import React, { useState, useEffect } from "react";
import axios from "axios";
import { Users as UsersIcon, Plus, Trash2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, staff: null });
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    role: "groups"
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async () => {
    if (!formData.name || !formData.code || formData.code.length !== 4) {
      toast.error("Please fill all fields (code must be 4 digits)");
      return;
    }

    try {
      await axios.post(`${API}/staff`, formData);
      toast.success(`Staff member ${formData.name} added successfully`);
      setAddModal(false);
      setFormData({ name: "", code: "", role: "groups" });
      fetchStaff();
    } catch (error) {
      console.error("Error adding staff:", error);
      if (error.response?.status === 409 || error.response?.data?.detail?.includes("duplicate")) {
        toast.error("Staff code already exists");
      } else {
        toast.error("Failed to add staff member");
      }
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteModal.staff) return;

    try {
      await axios.delete(`${API}/staff/${deleteModal.staff.id}`);
      toast.success(`Removed ${deleteModal.staff.name}`);
      setDeleteModal({ open: false, staff: null });
      fetchStaff();
    } catch (error) {
      console.error("Error deleting staff:", error);
      toast.error("Failed to delete staff member");
    }
  };

  const getRoleBadge = (role) => {
    if (role === "manager") {
      return <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30">Manager</Badge>;
    }
    return <Badge className="bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/30">Groups</Badge>;
  };

  return (
    <div className="h-full flex flex-col bg-[#09090B]">
      {/* Header */}
      <div className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight font-mono flex items-center gap-3">
                <Shield className="w-8 h-8 text-[#22C55E]" />
                Manage Staff
              </h1>
              <p className="text-sm text-[#A1A1AA] mt-1">Add or remove staff members</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="glass-card px-4 py-2 rounded-lg">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Staff</p>
                <p className="text-2xl font-bold font-mono text-[#22C55E]">{staff.length}</p>
              </div>
              <Button
                onClick={() => setAddModal(true)}
                className="bg-[#22C55E] hover:bg-[#22C55E]/90 shadow-[0_0_10px_rgba(34,197,94,0.2)]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Grid */}
      <div className="flex-1 overflow-auto scroll-container p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22C55E]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {staff.map((member) => (
              <div key={member.id} className="glass-card p-4 rounded-lg hover:bg-[#27272A]/30 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                      <UsersIcon className="w-6 h-6 text-[#22C55E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#FAFAFA]">{member.name}</h3>
                      <p className="text-xs text-[#A1A1AA] font-mono">Code: {member.code}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteModal({ open: true, staff: member })}
                    className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  {getRoleBadge(member.role)}
                  <span className="text-xs text-[#A1A1AA]">
                    {member.created_at && new Date(member.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent className="bg-[#18181B] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="font-mono text-xl">Add New Staff Member</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Create a new staff account with a unique 4-digit code
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#A1A1AA]">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#09090B] border-[#27272A] text-[#FAFAFA] mt-2"
                placeholder="Enter staff name"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Staff Code (4 digits) *</Label>
              <Input
                type="text"
                maxLength={4}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.replace(/\D/g, '') })}
                className="bg-[#09090B] border-[#27272A] text-[#FAFAFA] mt-2 font-mono text-center text-xl tracking-widest"
                placeholder="0000"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Role *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="bg-[#09090B] border-[#27272A] text-[#FAFAFA] mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181B] border-[#27272A]">
                  <SelectItem value="groups">Groups</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModal(false)} className="border-[#27272A]">
              Cancel
            </Button>
            <Button onClick={handleAddStaff} className="bg-[#22C55E] hover:bg-[#22C55E]/90">
              Add Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, staff: null })}>
        <DialogContent className="bg-[#18181B] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="font-mono text-xl text-[#EF4444]">Delete Staff Member?</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Are you sure you want to remove <strong className="text-[#FAFAFA]">{deleteModal.staff?.name}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, staff: null })}
              className="border-[#27272A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteStaff}
              className="bg-[#EF4444] hover:bg-[#EF4444]/90"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageStaff;
